import { useEffect, useState } from "react";
import { useErrorHandler } from "./useErrorHandler";
import { MeterWeeklyConsumptionType } from "../types";
import {
  fetchDailyConsumptionAsync,
  fetchWeeklyConsumptionAsync,
} from "../actions/meters.actions";
import { useMeterStore } from "../store/meter.store";

export default function useMeter(meterId: string) {
  const { handleError } = useErrorHandler();

  const { setMeterSidebarSelector } = useMeterStore((state) => state);

  const [weeklyConsumption, setWeeklyConsumption] = useState<
    MeterWeeklyConsumptionType[]
  >([]);
  const [dailyConsumption, setDailyConsumption] = useState<
    {
      time: string;
      date: string;
      fullLabel: string;
      consumption: number;
      hourIndex: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingDailyConsumption, setLoadingDailyConsumption] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);

  function generateTimeConsumption(data: number[], startDate: string) {
    if (!data?.length) return [];

    const result = [];
    const start = new Date(startDate);
    const dataLength = data.length;

    // Process all available hours (up to 96 for 96h telegram)
    for (let i = 0; i < dataLength; i++) {
      const currentTime = new Date(start.getTime() - i * 60 * 60 * 1000);
      const hour = currentTime.getUTCHours();
      const day = currentTime.getUTCDate();
      const month = currentTime.getUTCMonth() + 1;

      // Format time with 12-hour clock
      const timeStr =
        hour === 0
          ? "12 AM"
          : hour < 12
          ? `${hour} AM`
          : hour === 12
          ? "12 PM"
          : `${hour - 12} PM`;

      // Include date for multi-day data
      const dateStr = `${day}/${month}`;

      result.push({
        time: timeStr,
        date: dateStr,
        fullLabel: `${timeStr}\n${dateStr}`,
        consumption: data[dataLength - 1 - i] || 0,
        hourIndex: i,
      });
    }

    // Reverse to order from oldest to newest
    return result.reverse();
  }

  const fetchWeeklyConsumption = async () => {
    setLoading(true);

    fetchWeeklyConsumptionAsync(meterId)
      .then((weeklyConsumptionData: any) => {
        const reversedArray = (weeklyConsumptionData?.data || []).reverse();

        setWeeklyConsumption(reversedArray);

        if (reversedArray?.length) {
          let entryId = reversedArray[reversedArray.length - 1]?.entryID;

          if (entryId) {
            setSelectedEntryId(entryId);
            fetchDailyConsumptionAsync(entryId)
              .then((dailyConsumptionData: any) => {
                let timeConsumption = generateTimeConsumption(
                  dailyConsumptionData?.data,
                  reversedArray[reversedArray.length - 1]?.deviceTime
                );

                setDailyConsumption(timeConsumption);
              })
              .catch((error) => {
                handleError(error);
              });
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);

        handleError(error);
      });
  };

  const handleEntrySelect = async (entryId: number) => {
    setLoadingDailyConsumption(true);
    setSelectedEntryId(entryId);

    let meter = weeklyConsumption?.find((item) => item.entryID === entryId);

    fetchDailyConsumptionAsync(entryId)
      .then((dailyConsumptionData: any) => {
        let timeConsumption = generateTimeConsumption(
          dailyConsumptionData?.data,
          meter?.deviceTime || new Date().toLocaleString()
        );

        setDailyConsumption(timeConsumption);

        setLoadingDailyConsumption(false);
      })
      .catch((error) => {
        handleError(error);

        setLoadingDailyConsumption(false);
      });
  };

  useEffect(() => {
    if (meterId) {
      fetchWeeklyConsumption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meterId]);

  return {
    weeklyConsumption,
    dailyConsumption,
    loading,
    handleEntrySelect,
    loadingDailyConsumption,
    setMeterSidebarSelector,
    selectedEntryId,
  };
}
